<template>
  <div class="w-full bg-root card p-8">
    <div>
      <div class="flex justify-between items-center mb-5">
        <p class="text-xs font-bold">Transfer Details</p>
        <p @click="edit" class="cursor-pointer text-primary">Edit</p>
      </div>
      <div class="flex justify-between items-center mb-3">
        <p class="text-xs font-normal">
          {{
            hasBeenDebited
              ? 'You have been debited exactly'
              : 'You would be debited exactly'
          }}
        </p>
        <p class="text-base font-bold">
          {{
            fromBalance
              ? parseMoney(transferDetails.debitAmount)
              : parseMoneyAll(
                  transferDetails.debitAmount +
                    transferDetails.partnerCommission,
                  country.currency
                ) || 0
          }}
        </p>
      </div>
      <div class="flex justify-between items-center mb-3">
        <p class="text-xs font-normal">Our fees</p>
        <p class="text-xs font-normal">
          {{
            fromBalance
              ? parseMoney(transferDetails.fees)
              : parseMoneyAll(transferDetails.fees, country.currency) || 0
          }}
        </p>
      </div>
      <div
        class="flex justify-between items-center mb-3"
        v-if="!fromBalance && transferDetails.partnerCommission"
      >
        <p class="text-xs font-normal">My Commission</p>
        <p class="text-xs font-normal">
          {{
            parseMoneyAll(transferDetails.partnerCommission, country.currency)
          }}
        </p>
      </div>
      <div v-if="!fromBalance" class="flex justify-between items-center mb-3">
        <p class="text-xs font-normal">Exchange rate</p>
        <p class="text-xs font-normal">
          1
          {{ !this.isDivisionOperator ? activeCurrency : secondaryCurrency }} =
          {{ exchangeRateBase }}
          {{ !this.isDivisionOperator ? secondaryCurrency : activeCurrency }}
        </p>
      </div>
      <div class="flex justify-between mb-3">
        <p class="text-xs font-normal">Amount converted</p>
        <p class="text-xs font-normal">
          {{
            parseMoneyAll(
              transferDetails.debitAmount - transferDetails.fees,
              country.currency
            )
          }}
        </p>
      </div>
      <div class="flex justify-between items-center mb-3">
        <p class="text-xs capitalize font-normal">
          {{ recipientInfo.recipientName || 'Recipient' }} gets
        </p>
        <p class="text-base font-bold max-w-xs lg:max-w-none">
          {{ convertValueTo2DP(transferDetails.recipientAmount || 0) }}
          {{ country2.currency }}
        </p>
      </div>
      <hr />
      <div class="flex justify-between items-center mt-5 mb-5">
        <p class="text-xs font-bold">
          Recipient
          {{ !transferDestinationIsAsset ? 'Bank' : 'Wallet' }} Details
        </p>
        <p @click="changeBankDetails" class="cursor-pointer text-primary">
          Change
        </p>
      </div>
      <div v-if="!transferDestinationIsAsset">
        <div class="flex justify-between items-center mb-3">
          <p class="text-xs font-normal">Bank Name</p>
          <p class="text-xs font-normal">{{ recipientInfo.bankName || '' }}</p>
        </div>
        <div class="flex justify-between items-center mb-3">
          <p class="text-xs font-normal">Account Number</p>
          <p class="text-xs font-normal">
            {{ recipientInfo.accountNo || '' }}
          </p>
        </div>
        <div class="flex justify-between items-center mb-3 mt-3">
          <p class="text-xs font-normal">Account Name</p>
          <p class="text-xs font-normal">
            {{ recipientInfo.accountName || '' }}
          </p>
        </div>
        <div
          v-if="recipientInfo.bankCountry === 'GH'"
          class="flex justify-between items-center mb-3 mt-3"
        >
          <p class="text-xs font-normal">Bank Branch</p>
          <p class="text-xs font-normal">
            {{ recipientInfo.bankBranch || '' }}
          </p>
        </div>
        <div
          v-if="!transferDestinationIsLocal"
          class="flex justify-between items-center mb-3"
        >
          <p class="text-xs font-normal">Bank Address</p>
          <p class="text-xs font-normal">
            {{ recipientInfo.bankAddress || '' }}
          </p>
        </div>
        <div
          v-if="isAmericanDetails"
          class="flex justify-between items-center mb-3"
        >
          <p class="text-xs font-normal">Postal code</p>
          <p class="text-xs font-normal">
            {{ recipientInfo.bankPostalCode || '' }}
          </p>
        </div>
        <div
          v-if="transferDestinationIsGlobalFiat && recipientInfo.swiftCode"
          class="flex justify-between items-center mb-3"
        >
          <p class="text-xs font-normal">Swift code</p>
          <p class="text-xs font-normal">
            {{ recipientInfo.swiftCode || '' }}
          </p>
        </div>
        <div>
          <el-collapse>
            <el-collapse-item
              v-if="intermediaryBankDetail"
              title="Intermediary Details"
              name="1"
            >
              <div class="flex justify-end mt-3">
                <p
                  class="text-primary cursor-pointer"
                  @click="changeRecipientInfoDetails"
                >
                  Change
                </p>
              </div>
              <div class="flex justify-between items-center mb-3 mt-3">
                <p class="text-xs font-normal">Name</p>
                <p class="text-xs font-normal">
                  {{ recipientInfo.intermediaryBankName || '' }}
                </p>
              </div>
              <div class="flex justify-between items-center mb-3 mt-3">
                <p class="text-xs font-normal">Address</p>
                <p class="text-xs font-normal">
                  {{ recipientInfo.intermediaryBankAddress || '' }}
                </p>
              </div>
              <div class="flex justify-between items-center mb-3 mt-3">
                <p class="text-xs font-normal">Bank Reference</p>
                <p class="text-xs font-normal">
                  {{ recipientInfo.intermediaryBankReference || '' }}
                </p>
              </div>
              <div class="flex justify-between items-center mb-3 mt-3">
                <p class="text-xs font-normal">Swift Code</p>
                <p class="text-xs text-root-4 font-normal">
                  {{ recipientInfo.intermediaryBankSwiftCode || '' }}
                </p>
              </div>
            </el-collapse-item>
            <el-collapse-item
              v-if="!transferDestinationIsLocal"
              title="Recipient Details"
              name="2"
            >
              <div>
                <div class="flex justify-end mt-3">
                  <p
                    class="text-primary cursor-pointer"
                    @click="changeRecipientInfoDetails"
                  >
                    Change
                  </p>
                </div>
                <div class="flex justify-between items-center mt-3 mb-3">
                  <p class="text-xs font-normal">Country</p>
                  <p class="text-xs font-normal">
                    {{ recipientInfo.recipientCountry || '' }}
                  </p>
                </div>
                <div class="flex justify-between items-center mb-3 mt-3">
                  <p class="text-xs font-normal">Name</p>
                  <p class="text-xs font-normal">
                    {{ recipientInfo.recipientName || '' }}
                  </p>
                </div>
                <div class="flex justify-between items-center mb-3 mt-3">
                  <p class="text-xs font-normal">Address</p>
                  <p class="text-xs font-normal">
                    {{ recipientInfo.recipientAddress || '' }}
                  </p>
                </div>
                <div class="flex justify-between items-center mb-3">
                  <p class="text-xs font-normal">City</p>
                  <p class="text-xs font-normal">
                    {{ recipientInfo.recipientCity || '' }}
                  </p>
                </div>
                <div
                  v-if="isAmericanDetails"
                  class="flex justify-between items-center mb-3"
                >
                  <p class="text-xs font-normal">Postal Code</p>
                  <p class="text-xs font-normal">
                    {{ recipientInfo.recipientPostalCode || '' }}
                  </p>
                </div>
              </div>
            </el-collapse-item>
          </el-collapse>
        </div>
      </div>
      <div v-else>
        <div class="flex justify-between items-center mb-3 mt-3">
          <p class="text-xs font-normal">Network</p>
          <p class="text-xs font-normal">
            {{ parseNetworkName(recipientInfo.network) || '' }}
          </p>
        </div>
        <div class="flex justify-between items-center mb-3 mt-3">
          <p class="text-xs font-normal">Asset</p>
          <p class="text-xs font-normal">
            {{ recipientInfo.asset || '' }}
          </p>
        </div>
        <div class="flex justify-between items-center mb-3 mt-3">
          <p class="text-xs font-normal">Wallet Address</p>
          <p class="text-xs font-normal">
            {{ recipientInfo.walletAddress || '' }}
          </p>
        </div>
        <div class="flex justify-between items-center mb-3 mt-3">
          <p class="text-xs font-normal">Reference</p>
          <p class="text-xs font-normal">
            {{ recipientInfo.reference || '' }}
          </p>
        </div>
      </div>
      <div class="flex justify-start items-center mt-5 mb-5">
        <p class="text-xs font-bold">Estimated time of arrival</p>
      </div>
      <div class="flex justify-between items-center mb-3">
        <p class="text-xs font-normal">Sending</p>
        <p class="text-xs font-normal">Now</p>
      </div>
      <div class="flex justify-between items-center mb-3">
        <p class="text-xs font-normal">Should arrive</p>
        <p class="text-xs font-normal">
          {{ transferDestinationIsAsset ? '60 minutes' : 'In 2 days' }}
        </p>
      </div>
      <hr />
      <div class="mt-5">
        <form>
          <div class="flex flex-col">
            <label class="text-xs font-normal">Note (optional)</label>
            <input
              class="h-14 border pl-4 mt-1 bg-root rounded-md"
              placeholder="Enter Reference"
              v-model="note"
            />
          </div>
          <div class="pt-5">
            <o-button
              :loading="isLoading"
              @click="authorize()"
              class="text-white"
            >
              {{
                isLoading
                  ? 'Initiating...'
                  : fromBalance
                  ? 'Initiate Transfer'
                  : 'Next'
              }}</o-button
            >
          </div>
        </form>
      </div>
    </div>
    <!-- auth dialog -->
    <verify-auth-dialog
      v-if="showVerifyAuthDialog"
      :authState="authState"
      :showDialog="showVerifyAuthDialog"
      @closeDialog="closeAuthDialog"
      @verifiedUser="verifiedUserTerminalTransfer"
    />
    <transaction-processing
      v-if="showTransferProcessingDialog"
      :showDialog="showTransferProcessingDialog"
      @closeDialog="closeTransferProcessingDialog"
    />
    <deposit-Wallet
      v-if="showDepositWalletDialog"
      :showDialog="showDepositWalletDialog"
      @closeDialog="closeDepositWalletDialog"
      @next="initiateTransferHandler"
      @updateAssetTransferDetails="goToEnterAmount"
      @showMakePayment="goToMakePayment"
    />
  </div>
</template>

<script>
import {
  ParseMoney,
  ConvertValueToXDP,
  ParseMoneyNaira,
  ParseMoneyLocal,
  ParseMoneyAll,
  ParseNetworkName
} from '@/utils/helpers'
import exchangeRate from '@/mixins/localDeposit.js'
import country from '@/mixins/getCountryDetails.js'
export default {
  mixins: [exchangeRate, country],
  name: 'ReviewDetails',
  components: {
    VerifyAuthDialog: () => import('@/components/auth/VerifyAuthDialog'),
    TransactionProcessing: () => import('../TransactionProcessing.vue'),
    DepositWallet: () =>
      import('@/components/terminal/dialogs/DepositWallet.vue')
  },
  data () {
    return {
      showVerifyAuthDialog: false,
      showTransferProcessingDialog: false,
      showDepositWalletDialog: false,
      authState: 'transferTerminal',
      isLoading: false,
      note: '',
      walletDetails: {},
      destinationDetails: {}
    }
  },
  methods: {
    closeDepositWalletDialog () {
      this.showDepositWalletDialog = false
    },
    convertValueTo2DP (val) {
      return ConvertValueToXDP(val, 2)
    },
    parseMoneyAll (val, currency) {
      return ParseMoneyAll(val, currency)
    },
    parseMoney (val) {
      return ParseMoney(val)
    },
    parseMoneyNaira (val) {
      return ParseMoneyNaira(val)
    },
    parseMoneyLocal (val, symbol) {
      return ParseMoneyLocal(val, symbol)
    },
    authorize () {
      if (this.fromBalance) {
        this.showVerifyAuthDialog = true
      } else if (this.hasBeenDebited) {
        this.initiateTransferAsset()
      } else if (this.transferSourceIsAsset) {
        this.showDepositWalletDialog = true
      } else {
        this.initiateTransferLocal()
      }
    },
    initiateTransferHandler () {
      this.next()
      // this.initiateTransferLocal()
    },
    closeAuthDialog () {
      this.showVerifyAuthDialog = false
    },
    closeTransferProcessingDialog () {
      this.showTransferProcessingDialog = false
    },
    async verifiedUserTerminalTransfer (twoFaCode) {
      await this.getDetails()
      this.isLoading = true
      const vm = this
      let details = {}
      if (!this.transferSourceIsGlobalFiat) {
        details = {
          amount: this.transferDetails.debitAmount,
          destinationCurrency: this.country2.currency,
          note: this.note,
          reason: this.transferDetails.transferReason
          // destination: this.transferDestinationIsAsset
          //   ? this.walletDetailsgit pull --rebase
          //   : this.destinationDetails
        }
        if (this.usedBeneficiary) {
          details.beneficiaryID = this.beneficiaryID
        } else {
          details.destination = this.transferDestinationIsAsset
            ? this.walletDetails
            : this.destinationDetails
        }
        if (!this.fromBalance) {
          details.localDeposit = {
            amount: this.transferDetails.debitAmount,
            currency: this.country.currency
          }
          if (this.transferDetails.partnerCommission) {
            details.businessCommission = this.transferDetails.partnerCommission
          }
        } else {
          details.twoFaCode = twoFaCode.code
        }
      } else {
        const bankDetails = {
          bankDetails: {
            accountNumber: this.recipientInfo.accountNo,
            bankName: this.recipientInfo.bankName,
            bankCode: this.recipientInfo.bankCode,
            accountName: this.recipientInfo.accountName,
            country: this.recipientInfo.bankCountry,
            bankBranch: this.recipientInfo.bankBranch,
            isWithinUs: 'no'
          }
        }
        details = {
          // amount: this.transferDetails.debitAmount,
          source: 'fx_transfer',
          note: this.note,
          reason: this.transferDetails.transferReason,
          destinationCurrency: this.country2.currency,
          // destination: this.transferDestinationIsAsset
          //   ? this.walletDetails
          //   : bankDetails,
          fxDeposit: {
            amount: this.transferDetails.debitAmount,
            currency: 'USD'
            // localCurrency: this.country2.currency
          }
        }
        if (this.usedBeneficiary) {
          details.beneficiaryID = this.beneficiaryID
        } else {
          details.destination = this.transferDestinationIsAsset
            ? this.walletDetails
            : bankDetails
        }
      }
      this.$store
        .dispatch('terminal/initiateTransfer', details)
        .then(() => {
          this.isLoading = false
          if (this.fromBalance) {
            this.closeAuthDialog()
            this.showTransferProcessingDialog = true
          } else {
            this.$store.dispatch(
              'transactions/setCurrentTransaction',
              this.$store.state.terminal.transferInfo.transfer || {}
            )
            this.$store.dispatch(
              'transactions/setTransactionDetails',
              this.$store.state.terminal.transferInfo.transfer || {}
            )
            this.next()
          }
        })
        .catch((error) => {
          this.showVerifyAuthDialog = false
          const err = error.response
            ? error.response.data.error.publicMessage
            : error
          vm.$swal({
            icon: 'error',
            title: 'Oops',
            text: err,
            iconColor: this.$primaryColor,
            allowOutsideClick: false
          })
        })
        .finally(() => {
          this.isLoading = false
          this.showVerifyAuthDialog = false
        })
    },
    getDetails () {
      this.walletDetails = {
        walletDetails: {
          assetType: this.country2.currency,
          walletAddress: this.recipientInfo.walletAddress,
          network: 'ETH'
        }
      }
      this.destinationDetails = {
        bankDetails: {
          accountNumber: this.recipientInfo.accountNo,
          routingNumber: this.recipientInfo.routingNo,
          bankName: this.recipientInfo.bankName,
          country: this.recipientInfo.bankCountry,
          accountName: this.recipientInfo.accountName,
          bankCode: this.recipientInfo.bankCode,
          city: this.recipientInfo.bankCity,
          bankAddress: this.recipientInfo.bankAddress,
          swiftCode: this.recipientInfo.swiftCode,
          bankBranch: this.recipientInfo.bankBranch,
          district: this.recipientInfo.bankDistrict,
          postalCode: this.recipientInfo.bankPostalCode,
          isWithinUs: this.recipientInfo.bankCountry === 'US' ? 'yes' : 'no',
          intermediaryBank:
            this.recipientInfo.bankCountry === 'US' ||
            this.transferSourceIsGlobalFiat
              ? null
              : {
                  bankName: this.recipientInfo.intermediaryBankName,
                  bankAddress: this.recipientInfo.intermediaryBankAddress,
                  reference: this.recipientInfo.intermediaryBankReference,
                  swiftCode: this.recipientInfo.intermediaryBankSwiftCode
                }
        },
        // personalDetails: {
        //   name: this.recipientInfo.recipientName,
        //   country: this.recipientInfo.recipientCountry,
        //   city: this.recipientInfo.recipientCity,
        //   address: this.recipientInfo.recipientAddress,
        //   district: this.recipientInfo.recipientDistrict,
        //   postalCode: this.recipientInfo.recipientPostalCode
        // },
        intermediaryBank: !this.recipientInfo.intermediaryBankName
          ? null
          : {
              bankName: this.recipientInfo.intermediaryBankName,
              bankAddress: this.recipientInfo.intermediaryBankAddress,
              reference: this.recipientInfo.intermediaryBankReference,
              swiftCode: this.recipientInfo.intermediaryBankSwiftCode
            }
      }
      if (
        !this.transferDestinatinationIsAsset &&
        !this.transferDestinationIsLocal
      ) {
        this.destinationDetails.personalDetails = {
          name: this.recipientInfo.recipientName,
          country: this.recipientInfo.recipientCountry,
          city: this.recipientInfo.recipientCity,
          address: this.recipientInfo.recipientAddress,
          district: this.recipientInfo.recipientDistrict,
          postalCode: this.recipientInfo.recipientPostalCode
        }
      }
    },
    async initiateTransferAsset () {
      this.isLoading = true
      await this.getDetails()
      const vm = this
      const details = {
        assetDepositID: this.assetDepositId,
        destinationCurrency: this.country2.currency,
        note: this.note,
        reason: this.transferDetails.transferReason
        // beneficiaryDetails: this.transferDestinationIsAsset
        //   ? this.walletDetails
        //   : this.destinationDetails
      }
      if (this.usedBeneficiary) {
        details.beneficiaryID = this.beneficiaryID
      } else {
        details.beneficiaryDetails = this.transferDestinationIsAsset
          ? this.walletDetails
          : this.destinationDetails
      }
      this.$store
        .dispatch('terminal/initiateAssetTransfer', details)
        .then(() => {
          this.isLoading = false
          if (this.fromBalance) {
            this.closeAuthDialog()
            this.showTransferProcessingDialog = true
          } else {
            this.$store.dispatch(
              'transactions/setCurrentTransaction',
              this.$store.state.terminal.transferInfo.transfer || {}
            )
            this.$store.dispatch(
              'transactions/setTransactionDetails',
              this.$store.state.terminal.transferInfo.transfer || {}
            )
            this.$emit('showMakePayment')
          }
        })
        .catch((error) => {
          this.showVerifyAuthDialog = false
          const err = error.response
            ? error.response.data.error.publicMessage
            : error
          vm.$swal({
            icon: 'error',
            title: 'Oops',
            text: err,
            iconColor: this.$primaryColor,
            allowOutsideClick: false
          })
        })
        .finally(() => {
          this.isLoading = false
          this.showVerifyAuthDialog = false
        })
    },
    initiateTransferLocal () {
      // this.next()
      this.verifiedUserTerminalTransfer()
    },
    async goToMakePayment () {
      await this.initiateTransferAsset()
    },
    goToEnterAmount () {
      this.$emit('updateAssetTransferDetails')
    },
    next () {
      this.$emit('next')
    },
    edit () {
      this.$emit('editTransfer')
    },
    changeRecipientInfoDetails () {
      this.$emit('changeUserDetails')
    },
    changeBankDetails () {
      this.$store.dispatch('terminal/beneficiaryOption', false)
      this.$store.dispatch('terminal/usedBenificiary', false)
      this.$emit('changeBank')
    },
    parseNetworkName (networkType) {
      return ParseNetworkName(networkType)
    }
  },
  computed: {
    hasBeenDebited () {
      return this.$store.state.terminal.hasBeenDebited
    },
    beneficiaryID () {
      return this.$store.state.terminal.beneficiaryID
    },
    usedBeneficiary () {
      return this.$store.state.terminal.usedBeneficiary
    },
    recipientInfo () {
      return (
        this.$store.state.terminal.transferTerminalDetails
          .recipientInformation || {}
      )
    },
    transferDetails () {
      return (
        this.$store.state.terminal.transferTerminalDetails.transferDetails || {}
      )
    },
    assetDepositId () {
      return this.$store.state.terminal.assetDepositId
    },
    fromBalance () {
      return this.$store.state.terminal.terminalTransferType === 'fromBalance'
    },
    intermediaryBankDetail () {
      return this.$store.state.terminal.transferTerminalDetails
        .recipientInformation.intermediaryBankName
    },
    isAmericanDetails () {
      return (
        this.recipientInfo.bankCountry === 'US' ||
        !this.recipientInfo.bankCountry
      )
    }
  }
}
</script>

<style scoped>
.card {
  box-shadow:
    0px 0px 1px rgba(3, 2, 7, 0.1),
    0px 4px 8px rgba(3, 2, 7, 0.1);
  border-radius: 15px;
}
</style>
